import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../i18n';
import { blogPosts, categories, getPostsByCategory, getCategoryName } from '../data/blogPosts';
import './Blog.css';

const Blog = () => {
  const { t, language } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('todos');
  const filteredPosts = getPostsByCategory(activeCategory);
  const business = process.env.REACT_APP_SITE_NAME || 'SeekWebs';

  const localeMap = {
    pt: 'pt-BR',
    en: 'en-US',
    es: 'es-ES'
  };

  return (
    <>
      <Helmet>
        <title>{t('blog.title')} - {business}</title>
        <meta name="description" content={t('blog.subtitle')} />
      </Helmet>

      <div className="blog-page">
        <motion.header
          className="blog-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="back-link">
            <span className="back-arrow">←</span>
            {t('common.back')}
          </Link>
          <h1>{t('blog.title')}</h1>
          <p className="blog-subtitle">{t('blog.subtitle')}</p>
        </motion.header>

        <motion.nav
          className="blog-categories"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <button
            className={`category-btn ${activeCategory === 'todos' ? 'active' : ''}`}
            onClick={() => setActiveCategory('todos')}
          >
            {t('common.all')}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(category.id)}
            >
              <span className="category-icon">{category.icon}</span>
              {t(`blog.categories.${category.id}`) || category.name}
            </button>
          ))}
        </motion.nav>

        <motion.div
          className="blog-posts-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {filteredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              className="blog-post-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              <Link to={`/blog/${post.slug}`} className="post-card-link">
                <div className="post-card-image">
                  <img src={post.image} alt={post.title} loading="lazy" />
                  <span className="post-card-category">{getCategoryName(post.category)}</span>
                </div>
                <div className="post-card-content">
                  <h2 className="post-card-title">{post.title}</h2>
                  <p className="post-card-description">{post.description}</p>
                  <div className="post-card-footer">
                    <div className="post-card-author">
                      <span className="author-name">{post.author}</span>
                    </div>
                    <div className="post-card-meta">
                      <span className="post-date">{new Date(post.date).toLocaleDateString(localeMap[language] || 'pt-BR')}</span>
                      <span className="post-read-time">{post.readTime}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </motion.div>

        {filteredPosts.length === 0 && (
          <motion.div
            className="no-posts"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <p>{t('blog.noPostsFound')}</p>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default Blog;
